import { useEffect } from 'react'

import { getAIConversationMessages } from '../../../../api'
import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  EMPTY_OBJECT,
} from '../../../../constants/common'
import { useApi } from '../../../../hooks'
import { isApiNotFoundStatus } from '../../../../utils/api'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import {
  getGretaAIChatErrorStatus,
  getIsGretaAIChatFeedTurnInFlight,
} from '../../GretaAIChat.utils'

import { useGretaAIChatDeliveredTurns } from './useGretaAIChatDeliveredTurns'
import { GRETA_AI_CHAT_HISTORY_PARAMS } from './useGretaAIChatHistory.constants'
import {
  getGretaAIChatLoadedStatus,
  getIsGretaAIChatHistoryFailed,
  getIsGretaAIChatHistoryLoadable,
  mapGretaAIChatMessages,
  reconcileGretaAIChatLoadedMessages,
} from './useGretaAIChatHistory.utils'
import { useGretaAIChatOlderMessages } from './useGretaAIChatOlderMessages'

export function useGretaAIChatHistory({
  activeChatId,
  getIsTimeoutStopPending = EMPTY_FUNCTION,
  getIsTurnInFlight = EMPTY_FUNCTION,
  loadedChatIdRef,
  setMessages,
  setStatus,
  syncTimedOutTurn = EMPTY_FUNCTION,
  onChatMissing = EMPTY_FUNCTION,
}) {
  const messagesApi = useApi()

  // Only while the feed shows this chat — a load still bringing it checks the turn itself.
  function syncDeliveredTurn(id) {
    if (loadedChatIdRef.current === id) {
      messagesApi.call(({ options }) =>
        loadMessages({ id, options, silent: true })
      )
    }
  }

  const {
    cancelDeliveredTurnSync,
    forgetDeliveredTurn,
    getDeliveredTurn,
    getIsDeliveredTurnLagging,
    scheduleDeliveredTurnSync,
    trackDeliveredTurn,
  } = useGretaAIChatDeliveredTurns({ onSync: syncDeliveredTurn })

  const { abortOlderMessages, loadOlderMessages, resetOlderMessages } =
    useGretaAIChatOlderMessages({ activeChatId, setMessages })

  async function loadMessages({ id = activeChatId, options, silent = false }) {
    if (!silent) {
      // Marks "not loaded yet" so an interrupted load doesn't look already-loaded on return.
      loadedChatIdRef.current = EMPTY_OBJECT
      setStatus(
        getIsGretaAIChatFeedTurnInFlight(id, {
          getIsTimeoutStopPending,
          getIsTurnInFlight,
        })
          ? GretaAIChatStatus.Thinking
          : GretaAIChatStatus.Loading
      )
      setMessages(EMPTY_LIST)
      resetOlderMessages()
    }

    const deliveredTurn = getDeliveredTurn(id)

    try {
      const response = await getAIConversationMessages({
        id,
        options,
        params: GRETA_AI_CHAT_HISTORY_PARAMS,
      })

      if (response) {
        const loaded = mapGretaAIChatMessages(response)

        // A newer turn started meanwhile — its own sync brings the feed.
        if (silent && getDeliveredTurn(id) !== deliveredTurn) {
          return loaded
        }

        const isTurnLagging = getIsDeliveredTurnLagging(id, loaded)

        loadedChatIdRef.current = id

        if (silent && isTurnLagging) {
          return loaded
        }

        resetOlderMessages(response)
        setMessages(current =>
          reconcileGretaAIChatLoadedMessages({ current, isTurnLagging, loaded })
        )
        setStatus(
          getGretaAIChatLoadedStatus({
            isFailed: getIsGretaAIChatHistoryFailed(response),
            isTimedOut: syncTimedOutTurn(id, loaded),
            isTurnInFlight: getIsGretaAIChatFeedTurnInFlight(id, {
              getIsTimeoutStopPending,
              getIsTurnInFlight,
            }),
          })
        )

        return loaded
      }
    } catch (reason) {
      // Aborted by a chat switch — the newly active chat's load already owns status/messages.
      if (!options?.signal?.aborted) {
        if (isApiNotFoundStatus(reason?.response?.status)) {
          onChatMissing()
          setStatus(GretaAIChatStatus.Idle)
        } else if (silent) {
          scheduleDeliveredTurnSync(id)
        } else {
          const errorStatus = await getGretaAIChatErrorStatus(reason)

          // Classification can wait up to 500ms — a chat switch during it may
          // have already aborted this request; re-check before applying.
          if (!options?.signal?.aborted) {
            setStatus(errorStatus)
          }
        }
      }
    }
  }

  // Re-fetches page 1 so the feed reflects the backend's history, not a local guess.
  // `id` lets a caller holding a stale closure (a deferred wait settling after the
  // active chat changed) target the conversation it actually means, not whatever
  // `activeChatId` was when that caller captured this function.
  function reloadMessages(
    id = activeChatId,
    { silent = false } = EMPTY_OBJECT
  ) {
    return messagesApi.call(({ options }) =>
      loadMessages({ id, options, silent })
    )
  }

  async function fetchMessages(id) {
    try {
      const response = await getAIConversationMessages({
        id,
        params: GRETA_AI_CHAT_HISTORY_PARAMS,
      })

      return response ? mapGretaAIChatMessages(response) : null
    } catch {
      return null
    }
  }

  useEffect(() => {
    if (
      getIsGretaAIChatHistoryLoadable(activeChatId, loadedChatIdRef.current)
    ) {
      messagesApi.call(loadMessages)
    } else if (!activeChatId && loadedChatIdRef.current) {
      loadedChatIdRef.current = null
      resetOlderMessages()
      setMessages(EMPTY_LIST)
      setStatus(GretaAIChatStatus.Idle)
    }

    return () => {
      messagesApi.abort()
      abortOlderMessages()
      cancelDeliveredTurnSync()
    }
  }, [activeChatId])

  function abortReloadMessages() {
    messagesApi.abort()
  }

  return {
    abortReloadMessages,
    fetchMessages,
    forgetDeliveredTurn,
    loadOlderMessages,
    reloadMessages,
    syncDeliveredTurn,
    trackDeliveredTurn,
  }
}
